import { useMemo, useEffect, useState, useRef } from "react";
import { Dropdown } from "../../../common/components/Input";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../common/state/store";
import {
  getMediaSearch,
  IMediaState,
  mediaConfigSelector,
  getOriginalMediaGalleryId,
  mediaSelector,
  getCurrentModeListGallery,
  setMediaSearch,
  resetMediaSearch,
  IMediaSearch,
  fetchFilteredMedias,
} from "../state/gallerySlice";
import { getNodeById } from "../../graph/state/graphLegacy";
import { getCurrentProject } from "../../projects/state/projectsSlice";
import { useTranslation } from "react-i18next";
import { getCurrentUser } from "../../profile/state/profileSlice";
import SortableDropdown, { DropdownOption } from "src/common/components/Input/SortableDropdown";
import MediaSearchBar from "./MediaSearchBar";
import { useAppDispatch } from "src/common/state/hooks";
import { MEDIA_LIST_COMPONENT_ID } from "../Gallery";
import type { PageChangeArgs } from "../../gallery/useGalleryPagination";

export const MediaSearchPanelContainer = ({
  nodeId,
  allowedMediaTypeOverride,
  projectGroupId,
  projectId,
  componentId = MEDIA_LIST_COMPONENT_ID,
  onSearchChanged,
}: {
  nodeId: string;
  allowedMediaTypeOverride?: string;
  projectGroupId?: number;
  projectId?: number;
  componentId?: string;
  onSearchChanged?: (args: PageChangeArgs) => void;
}) => {
  const dispatch = useDispatch();
  const [showMedia360TagFilter, setShowMedia360TagFilter] = useState<boolean>(false);

  const { config, currentMediaSearch, originalMediaId } = useSelector((state: RootState) => ({
    config: mediaConfigSelector(state),
    currentMediaSearch: getMediaSearch(state),
    originalMediaId: getOriginalMediaGalleryId(state),
  }));

  const currentNode =
    Number(nodeId) !== 0 ? useSelector((state: RootState) => getNodeById(state, nodeId)) : null;

  useEffect(() => {
    if (!allowedMediaTypeOverride) {
      if (!projectGroupId && !projectId) {
        dispatch(resetMediaSearch());
      }
      if (projectGroupId || projectId) {
        dispatch(
          setMediaSearch({
            mediaType: "all",
            ...(projectGroupId != null && { projectGroupId }),
            ...(projectId != null && { projectId }),
          } as Partial<IMediaSearch>),
        );
      }
    }
  }, [allowedMediaTypeOverride, projectGroupId, projectId, dispatch]);

  useEffect(() => {
    if (
      currentMediaSearch.media360tag === "video180stereo" &&
      currentMediaSearch.mediaType !== "video" &&
      currentMediaSearch.mediaType !== "all"
    ) {
      dispatch(setMediaSearch({ media360tag: "" }));
    }

    if (currentMediaSearch.mediaType === "audio" || currentMediaSearch.mediaType === "pdf") {
      setShowMedia360TagFilter(false);
    } else if (
      (currentMediaSearch.mediaType === "model3d" && currentNode?.modelName === "content-scene") ||
      nodeId === "new-project-group-menu-sphere-app"
    ) {
      setShowMedia360TagFilter(false);
    } else {
      setShowMedia360TagFilter(true);
    }
  }, [currentMediaSearch.mediaType, currentNode, nodeId, dispatch]);

  return (
    <MediaSearchPanel
      nodeId={nodeId}
      mediaTypeFromGallery={currentMediaSearch.mediaType}
      config={config}
      allowedMediaTypeOverride={allowedMediaTypeOverride}
      showMedia360TagFilter={showMedia360TagFilter}
      originalMediaId={originalMediaId}
      projectGroupId={projectGroupId}
      projectId={projectId}
      componentId={componentId}
      onSearchChanged={onSearchChanged}
    />
  );
};

const MediaSearchPanel = ({
  config,
  mediaTypeFromGallery,
  nodeId,
  allowedMediaTypeOverride,
  showMedia360TagFilter,
  originalMediaId,
  projectGroupId,
  projectId,
  componentId = MEDIA_LIST_COMPONENT_ID,
  onSearchChanged,
}: {
  mediaTypeFromGallery: string;
  config: IMediaState["config"];
  nodeId: string;
  allowedMediaTypeOverride?: string;
  showMedia360TagFilter: boolean;
  originalMediaId: number;
  projectGroupId?: number;
  projectId?: number;
  componentId?: string;
  onSearchChanged?: (args: PageChangeArgs) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const searchMediaBarRef = useRef(null);
  const currentProject = useSelector(getCurrentProject);
  const nodeToEdit = useSelector((state: RootState) => getNodeById(state, nodeId));
  const allMedias = useSelector(mediaSelector);
  const getCurrentuser = useSelector(getCurrentUser);
  const currentModeListGallery = useSelector(getCurrentModeListGallery);
  const mediaSearch = useSelector(getMediaSearch);
  const marginForMediaFormat =
    Number(getCurrentuser?.preferred_language.id) === 1 ? "mr-2" : "mr-3";

  const [mediaTypes, setMediaTypes] = useState<string[]>([]);

  useEffect(() => {
    if (nodeId !== "0") {
      if (allowedMediaTypeOverride) {
        setMediaTypes(allowedMediaTypeOverride.split("|").map((v) => v.trim()));
      } else if (
        nodeToEdit?.modelName === "content-scene" ||
        nodeId === `newscene-${currentProject.id}`
      ) {
        if (mediaTypeFromGallery !== "model3d") {
          setMediaTypes(["video", "image"]);
          const media = allMedias.find((v) => Number(v.id) === Number(originalMediaId));
          if (media?.type) {
            dispatch(setMediaSearch({ mediaType: media.type }));
          }
        } else {
          setMediaTypes([mediaTypeFromGallery]);
        }
      } else {
        setMediaTypes([mediaTypeFromGallery]);
      }
    } else {
      setMediaTypes(["video", "image", "model3d", "pdf", "audio", "all", "avatar"]);
      // if (!mediaSearch?.projectGroupId && !mediaSearch?.projectId) {
      //   dispatch(setMediaSearch({ mediaType: "all" }));
      // }
    }
  }, [
    nodeId,
    allowedMediaTypeOverride,
    nodeToEdit,
    currentProject,
    mediaTypeFromGallery,
    allMedias,
    originalMediaId,
    dispatch,
  ]);

  // Fall back to the full list whenever the media snapshot hasn't populated
  // config.mediaType (still loading, fetch failed, or empty org). Must check
  // .length, not `??`: `[] ?? x === []` collapsed the dropdown to just "Tous".
  const fallbackTypes = config?.mediaType?.length
    ? config.mediaType
    : ["image", "video", "model3d", "pdf", "audio", "avatar"];

  const dropdownOptions = useMemo(() => {
    const disabledTooltipText = t(
      `pages.gallery.filters.mediaTypeUnavailableFor${
        nodeToEdit?.modelName === "content-scene" ? "Scene" : "Interaction"
      }`,
    );

    const base = [
      {
        value: "all",
        optionText: t("general.all"),
        disabled: !mediaTypes.includes("all"),
        disabledTooltipText,
      },
    ];

    return base.concat(
      fallbackTypes.map((type: string) => ({
        value: type,
        optionText: t(`pages.gallery.mediaType.${type}`),
        disabled: !mediaTypes.includes(type),
        disabledTooltipText,
      })),
    );
  }, [mediaTypes, config, t, nodeToEdit]);

  const get360TagFilters = (mediaType: string) => {
    const filters =
      mediaType === "model3d"
        ? [
            { value: "", optionText: t("general.all") },
            { value: "media360", optionText: t("pages.gallery.filters.environments3d") },
            { value: "others", optionText: t("pages.gallery.filters.objects3d") },
          ]
        : [
            { value: "", optionText: t("general.all") },
            { value: "media360", optionText: t("pages.gallery.filters.medias360") },
            { value: "media2d", optionText: t("pages.gallery.filters.medias2D") },
          ];

    if (mediaType === "video" || mediaType === "all") {
      filters.push({
        value: "video180stereo",
        optionText: t("pages.gallery.filters.video180"),
      });
    }

    return filters;
  };

  const handleDropdownChange = ({ field, value }: { field: keyof IMediaSearch; value: string }) => {
    if (mediaSearch[field] === value) {
      return;
    }

    const updatedSearch = {
      ...mediaSearch,
      [field]: value,
      page: 0,
      query: "",
      ...(projectGroupId != null && { projectGroupId }),
      ...(projectId != null && { projectId }),
    } as IMediaSearch;

    dispatch(setMediaSearch(updatedSearch));

    if (onSearchChanged) {
      onSearchChanged({
        newPage: 0,
        newSort: updatedSearch.sort,
        projectGroupId,
        projectId,
      });
      return;
    }

    dispatch(
      fetchFilteredMedias({
        start: 0,
        limit: updatedSearch.limit || mediaSearch.limit,
        sort: updatedSearch.sort,
        componentId,
        ...(projectGroupId != null && { projectGroupId }),
        ...(projectId != null && { projectId }),
      }),
    );
  };

  const sortDropdownOptions: DropdownOption[] = [
    { value: "0", optionText: `${t("pages.gallery.filters.orderBy")}...` },
    { value: "updated_at_desc", optionText: t("general.date_desc") },
    { value: "updated_at_asc", optionText: t("general.date_asc") },
    { value: "name_asc", optionText: t("general.alphanum_asc") },
    { value: "name_desc", optionText: t("general.alphanum_desc") },
  ];

  return (
    <div className="sticky z-10 flex flex-col mb-9">
      <div id="gallery-filter-container" className="flex items-center">
        <div className="mr-2 mb-[-4px]" ref={searchMediaBarRef}>
          <MediaSearchBar
            allowedMediaTypeOverride={allowedMediaTypeOverride}
            componentId={componentId}
          />
        </div>

        <div className="mr-2 format-dropdown-in-gallery">
          <p className="text-gray-500 font-medium mb-2">{t("pages.gallery.filters.format")}</p>
          <Dropdown
            borderOff
            width="lg:w-32 md:w-20 1735px:w-36 1735px:mr-2"
            options={dropdownOptions}
            value={mediaSearch.mediaType}
            onChangeCb={(value: string) => handleDropdownChange({ field: "mediaType", value })}
            asDefaultValue
            label=""
            disabled={mediaTypes.length === 1}
          />
        </div>

        {showMedia360TagFilter && (
          <div className={marginForMediaFormat}>
            <p className="text-gray-500 font-medium mb-2">{t("pages.gallery.filters.mediatype")}</p>
            <Dropdown
              borderOff
              width="w-[7.5rem] 1735px:w-48 1735px:mr-2"
              options={get360TagFilters(mediaSearch.mediaType)}
              value={mediaSearch.media360tag}
              onChangeCb={(value: string) => handleDropdownChange({ field: "media360tag", value })}
              asDefaultValue
              label=""
            />
          </div>
        )}

        <div className="mr-2">
          <p className="text-gray-500 font-medium mb-2">{t("general.property")}</p>
          <Dropdown
            borderOff
            width="md:w-20 lg:w-24 2xl:w-72 1735px:mr-2"
            options={[
              { value: "all", optionText: t("general.all") },
              { value: "public", optionText: t("general.media_Public") },
              { value: "private", optionText: t("general.media_Private") },
            ]}
            value={mediaSearch.owner}
            onChangeCb={(value: string) => handleDropdownChange({ field: "owner", value })}
            asDefaultValue
            label=""
          />
        </div>

        {!currentModeListGallery && (
          <div className="mr-2">
            <p className="text-gray-500 font-medium mb-2">{t("pages.gallery.filters.orderBy")}</p>
            <SortableDropdown
              options={sortDropdownOptions}
              search={mediaSearch}
              handleChange={(e) => handleDropdownChange({ field: "sort", value: e.target.value })}
            />
          </div>
        )}
      </div>
    </div>
  );
};
